/**
 * Design tokens are the seam between a template and a restaurant's brand.
 *
 * Nodes never carry a literal colour or font family, only a token key. Applying
 * a brand therefore means swapping the token table, and every blueprint written
 * against it re-themes correctly without the template author doing anything.
 */
export const DEFAULT_CONTRAST_RULES = [
    { text: 'onSurface', background: 'surface', minLc: 75 },
    { text: 'onSurfaceMuted', background: 'surface', minLc: 60 },
    { text: 'onAccent', background: 'accent', minLc: 60 },
];
const FALLBACK_FONT = {
    family: 'system-ui',
    source: 'system',
    weights: [400, 600],
    fallback: 'sans-serif',
};
/**
 * Restores token maps that storage layers may have dropped.
 *
 * Mongoose minimize (and similar) strips empty objects, so a valid
 * `shadows: {}` can come back as a missing key. Render and validation both
 * assume every DesignTokens field is present.
 */
export function ensureDesignTokenMaps(tokens) {
    const fonts = tokens?.fonts;
    return {
        colors: tokens?.colors ?? {},
        typography: tokens?.typography ?? {},
        spacing: tokens?.spacing ?? {},
        radii: tokens?.radii ?? {},
        borders: tokens?.borders ?? {},
        shadows: tokens?.shadows ?? {},
        fonts: {
            display: fonts?.display ?? FALLBACK_FONT,
            body: fonts?.body ?? FALLBACK_FONT,
            accent: fonts?.accent ?? FALLBACK_FONT,
        },
    };
}
export function isSpacingRef(value) {
    return typeof value === 'string' && value.startsWith('$');
}
/**
 * Canvas density applied at resolve time (`ResolvedTokens.duScale`).
 * Authored numeric design units are multiplied by this; spacing tokens are
 * already scaled in the token table.
 */
export function designUnitScale(tokens) {
    const scale = tokens.duScale;
    return typeof scale === 'number' && Number.isFinite(scale) && scale > 0 ? scale : 1;
}
/** Resolves a `Length` against the spacing scale; unknown tokens collapse to 0. */
export function resolveLength(value, spacing) {
    if (value == null)
        return 0;
    if (typeof value === 'number')
        return value;
    return spacing[value.slice(1)] ?? 0;
}
//# sourceMappingURL=tokens.js.map